// South African ID numbers (BR-06, PAT-05, PAT-07; modules-and-api.md §2.5). One place for the whole rule: 13 digits
// `YYMMDD SSSS C A Z`, Z a Luhn check digit over the first 12; the date of birth is YYMMDD with the century chosen so the
// person is between 0 and 120 years old (the most recent date not in the future); gender is Female for SSSS 0000–4999 and
// Male for 5000–9999; C is the citizenship digit (0 = citizen, 1 = permanent resident). A mismatch between what the number
// derives and what was typed is a WARNING, never a block (BR-06) — the derivation is offered, the entered value stands.
import { z } from "zod";
import { isValidSaId, type LocalDate } from "./primitives";

export const SA_ID_LENGTH = 13;
export const SA_ID_RE = /^\d{13}$/;
/** BR-06: the century is chosen so the age is within this range. */
export const SA_ID_MAX_AGE_YEARS = 120;

export type SaIdGender = "Male" | "Female";
export type SaIdCitizenship = "Citizen" | "PermanentResident" | "Other";

export interface SaIdDerivation {
  idNumber: string;
  dateOfBirth: LocalDate;
  gender: SaIdGender;
  /** Digits 7–10, the same-day registration sequence: < 5000 female, ≥ 5000 male. */
  sequence: number;
  /** Digit 11: 0 citizen, 1 permanent resident; any other digit is recorded as `Other`. */
  citizenship: SaIdCitizenship;
  citizenshipDigit: number;
  /** Full years at `today`. */
  age: number;
}

/** `YYYY-MM-DD` of a UTC date. */
const localDateOf = (d: Date): LocalDate => d.toISOString().slice(0, 10) as LocalDate;

/** The reference day as a UTC midnight, from a Date or a `YYYY-MM-DD` / ISO string. */
function dayOf(today: Date | string): Date {
  const d = typeof today === "string" ? new Date(today.length === 10 ? `${today}T00:00:00.000Z` : today) : today;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/** A calendar date built from its parts, or null when the parts do not form one (30 February). */
function calendarDate(year: number, month: number, day: number): Date | null {
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day ? d : null;
}

/** Full years between two `YYYY-MM-DD` dates (BR-07: age is computed, never stored). Negative when `to` precedes `from`. */
export function fullYearsBetween(from: string, to: string | Date): number {
  const a = dayOf(from);
  const b = dayOf(to);
  let years = b.getUTCFullYear() - a.getUTCFullYear();
  const beforeBirthday = b.getUTCMonth() < a.getUTCMonth() || (b.getUTCMonth() === a.getUTCMonth() && b.getUTCDate() < a.getUTCDate());
  if (beforeBirthday) years -= 1;
  return years;
}

/**
 * What a valid ID number says about its holder, or null when the number is not 13 digits, fails Luhn, or encodes no
 * calendar date within the last 120 years. `today` anchors the century choice and the age.
 */
export function parseSaId(idNumber: string, today: Date | string = new Date()): SaIdDerivation | null {
  if (!SA_ID_RE.test(idNumber) || !isValidSaId(idNumber)) return null;
  const yy = Number(idNumber.slice(0, 2));
  const month = Number(idNumber.slice(2, 4));
  const day = Number(idNumber.slice(4, 6));
  const ref = dayOf(today);
  // The most recent century whose date is not in the future (BR-06).
  const thisCentury = Math.floor(ref.getUTCFullYear() / 100) * 100;
  let dob: Date | null = null;
  for (const century of [thisCentury, thisCentury - 100, thisCentury - 200]) {
    const candidate = calendarDate(century + yy, month, day);
    if (candidate === null) return null;
    if (candidate.getTime() <= ref.getTime()) { dob = candidate; break; }
  }
  if (dob === null) return null;
  const dateOfBirth = localDateOf(dob);
  const age = fullYearsBetween(dateOfBirth, ref);
  if (age < 0 || age > SA_ID_MAX_AGE_YEARS) return null;
  const sequence = Number(idNumber.slice(6, 10));
  const citizenshipDigit = Number(idNumber[10]);
  return {
    idNumber,
    dateOfBirth,
    gender: sequence < 5000 ? "Female" : "Male",
    sequence,
    citizenship: citizenshipDigit === 0 ? "Citizen" : citizenshipDigit === 1 ? "PermanentResident" : "Other",
    citizenshipDigit,
    age,
  };
}

/**
 * A South African ID number as a request field: 13 digits, a valid Luhn check digit and a calendar date of birth. Issue
 * `params.code` is `digits`, `luhn` or `date` so the API can surface which rule failed. The derivations are a separate
 * call (`parseSaId`) so the field stays a plain string on the wire.
 */
export const saIdSchema = z.string().trim()
  .refine((s) => SA_ID_RE.test(s), { message: "must be 13 digits", params: { code: "digits" } })
  .refine((s) => !SA_ID_RE.test(s) || isValidSaId(s), { message: "not a valid South African ID number", params: { code: "luhn" } })
  .refine((s) => !SA_ID_RE.test(s) || !isValidSaId(s) || parseSaId(s) !== null, { message: "the ID number does not encode a date of birth", params: { code: "date" } });
export type SaId = z.infer<typeof saIdSchema>;

export interface SaIdWarning {
  path: "dateOfBirth" | "gender";
  code: "dob_mismatch" | "gender_mismatch";
  message: string;
  /** What the ID number derives. */
  derived: string;
}

/**
 * BR-06's non-blocking check: the entered date of birth or gender disagrees with the ID number. Nothing for a number that
 * does not parse (the schema has already refused it) or for fields that were not entered.
 */
export function saIdWarnings(idNumber: string, entered: { dateOfBirth?: string | null | undefined; gender?: string | null | undefined }, today: Date | string = new Date()): SaIdWarning[] {
  const parsed = parseSaId(idNumber, today);
  if (!parsed) return [];
  const out: SaIdWarning[] = [];
  if (entered.dateOfBirth && entered.dateOfBirth !== parsed.dateOfBirth) {
    out.push({ path: "dateOfBirth", code: "dob_mismatch", message: `the ID number gives a date of birth of ${parsed.dateOfBirth}`, derived: parsed.dateOfBirth });
  }
  if (entered.gender && entered.gender !== parsed.gender) {
    out.push({ path: "gender", code: "gender_mismatch", message: `the ID number gives a gender of ${parsed.gender}`, derived: parsed.gender });
  }
  return out;
}
